import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {useQuery, UseQueryResult} from '@tanstack/react-query';

import {CHECKOUT_DELIVERY_STEP} from 'constants/analytics/events/checkout';
import {ENDPOINTS} from 'constants/endpoints';
import {apiConfig} from 'libs/api/api';
import {capitalize} from 'libs/utils/utils';

import {autoSubscriptionEnabled} from './autoSubscriptionEnabled';
import {
    NEWSLETTER_SUBSCRIPTION_INFO_KEY,
    SUBSCRIPTION_STATUSES,
    SubscriptionStatusesType,
} from './constants';
import {managerNewsletterSubscription} from './managerNewsletterSubscription';

type PaypalAddressType = {
    admin_area_1: string;
    country_code: string;
};

/**
 * Function for fetching user's subscription status and sending the analytics event
 * @param {string} email - email
 * @returns {Promise<SubscriptionStatusesType>} - user's subcription status
 */
const fetchSubscriptionStatus = async (email: string): Promise<SubscriptionStatusesType> => {
    const {data} = await apiConfig.instanceGusaBackend({
        baseURL: apiConfig.API_URL,
        data: {
            destination: email,
            type: 'email',
        },
        method: 'post',
        url: ENDPOINTS.NEWSLETTER_SUBSCRIPTION_INFO,
        validateStatus: requestStatus =>
            (requestStatus >= 200 && requestStatus < 300) || requestStatus === 404,
    });

    let status = SUBSCRIPTION_STATUSES.UNKNOWN;

    if (data?.status === 1) status = SUBSCRIPTION_STATUSES.SUBSCRIBED;
    else if (data?.status === 2) status = SUBSCRIPTION_STATUSES.UNSUBSCRIBED;
    else if (data?.status === 3) status = SUBSCRIPTION_STATUSES.TRANSITIONAL;
    if (data?.message === 'Subscriber does not exists') status = SUBSCRIPTION_STATUSES.UNKNOWN;

    if (!managerNewsletterSubscription.getNextStep()) {
        const message =
            status === SUBSCRIPTION_STATUSES.UNKNOWN ? 'Unknown' : `Known - ${capitalize(status)}`;
        sendDesktopAnalyticsEvent(
            CHECKOUT_DELIVERY_STEP,
            {
                message: `ESP Status - ${message}`,
            },
            false,
        );
    }

    return status;
};

/**
 * Function for determining if the user can be auto subscribed
 * @param {string} email - user's email
 * @param {Object} shippingAddress - address
 * @returns {boolean} result
 */
export const shouldBeAutoSubscribed = async (
    email: string,
    shippingAddress: PaypalAddressType,
): Promise<boolean> => {
    const status = await fetchSubscriptionStatus(email);
    managerNewsletterSubscription.setSubscriptionStatus(status);

    const isAutoSubscriptionEnabled = autoSubscriptionEnabled(
        {
            shippingAddress: {
                country: {value: shippingAddress.country_code},
                state: {value: shippingAddress.admin_area_1},
            },
        },
        true,
    );

    return status === SUBSCRIPTION_STATUSES.UNKNOWN && isAutoSubscriptionEnabled;
};

/**
 * Fetches information about users subscription status to our newsletter. Returns the status of user's subscription
 * @param {string} email - user's email
 * @param {string} isUHC - is uhc enabled
 * @returns {UseQueryResult<SubscriptionStatusesType>} - subscription status
 */
export const useSubscriptionInfo = (
    email: string,
    isUHC: boolean,
): UseQueryResult<SubscriptionStatusesType> =>
    useQuery({
        enabled: !isUHC && Boolean(email),
        queryFn: () => fetchSubscriptionStatus(email),
        queryKey: [NEWSLETTER_SUBSCRIPTION_INFO_KEY, email],
        staleTime: 300000,
    });
